import { setup, assign } from 'xstate';
import { prepareSceneActor } from './studio.actors';
import { StudioContext, StudioEvent, StudioInput } from './studio.types';
import { cameraMachine } from '../camera/camera.fsm';
import { splatMachine } from '../splat/splat.fsm';
import { ObjectNode, SceneNode } from '@ad3d/schemas';

export const studioMachine = setup({
  types: {
    context: {} as StudioContext,
    events: {} as StudioEvent,
    input: {} as StudioInput,
    children: {} as {
      prepareScene: 'prepareSceneActor';
      camera: 'cameraMachine';
      splat: 'splatMachine';
    }
  },
  actors: {
    prepareSceneActor,
    cameraMachine,
    splatMachine,
  }
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5SwC4FcIEsD2A6ANtgIZYB2UAxBNqWLpqQG7YDWdADgE5jtHcDKAYzC0A2gAYAuolDtssTChykZIAB6IALAFZtuABwBObQEZtAdgBsl8YZPjNAGhABPROb37dm8QGZ9mta+vgBMIQC+4c6oGDgExGSUYJyc2Jy47PhEKABmaQC2Gdy8AsJiUqpyCko0qhoImiGWuIb6vpaNhiEmxiYmzm4IJvomuJZWDoEm5na+mvqR0ehYeIQkDEkpaRlZuQVFPHxgQiJgoibSSCBVisp1iL7azY2PmoaWXSM92gMPhpq4MxdUxtEKGKzTRYgGIrXDJVKcCgAJQAogAVJEATQA+gAZADyAEEACISS6yeS3WpXerDfS4cTaTQ6Yb-fzGSy-BBzUbBRkhXw9f7icQLKGkbAQOCqGE4SqUmoqGmIAC0Jl8uF0+hC4h6M10rT6XLVow6OnMfm+jO0wShstWCQ28uqd2V3IBzOmrXEOuZusMXO64lwoTBvnBfWMb00duWcW4JEGFJd1NA9XMjRDHh9mis+i8mn6rkQhmDoWZpksYKsINjsTw8LSzqpSrT7hMAMs+g8IRG7yM-y5wT0YKjvnM3cMpZMddhaFIRDQKAAFmlMAAvSDNxX3BDmXtZyyhfSWkIzcyBvyA7QhbRtczjzTj7WRSJAA */
  id: 'studio',
  initial: 'loading',
  context: ({ input }) => ({
    sceneId: input.sceneId,
    userId: input.userId,
    userRole: input.userRole || 'viewer',
    authToken: input.authToken,
    scene: null,
    permissions: { canEdit: false, canSave: false },
    error: null,
  }),
  states: {
    loading: {
      entry: () => console.log('Studio Machine: Loading...'),
      invoke: {
        id: 'prepareScene',
        src: 'prepareSceneActor',
        input: ({ context }) => ({ 
            sceneId: context.sceneId,
            userId: context.userId,
            userRole: context.userRole,
            authToken: context.authToken
        }),
        onDone: {
          target: 'ready',
          actions: assign({
            scene: ({ event }) => {
              const { permissions, ...scene } = event.output;
              console.log('Studio Machine: Scene Loaded', scene);
              return scene;
            },
            permissions: ({ event }) => event.output.permissions,
            error: null
          }),
        },
        onError: [
          {
            guard: ({ event }) => String(event.error).includes('403'),
            target: 'unauthorized',
            actions: assign({
              error: ({ event }) => {
                  console.error('Studio Machine: Unauthorized', event.error);
                  return String(event.error);
              }
            })
          },
          {
            target: 'error',
            actions: assign({
              error: ({ event }) => {
                  console.error('Studio Machine: Error', event.error);
                  return String(event.error);
              }
            })
          }
        ],
      },
    },
    ready: {
      entry: () => console.log('Studio Machine: Ready'),
      // Parallel states for different active subsystems
      type: 'parallel',
      states: {
        camera: {
          entry: () => console.log('Studio Machine: Invoking Camera'),
          invoke: {
            id: 'camera',
            src: 'cameraMachine',
            input: ({ context }) => {
              const activeCameraId = context.scene!.data.active_camera_id;
              const cameraNode = context.scene!.data.root.nodes.find(
                (n: SceneNode) => n.id === activeCameraId
              );

              if (cameraNode && cameraNode.type === 'camera_rig') {
                return {
                  position: [0, 1, 5], // TODO: Calculate from rig parameters or world_transform
                  target: [0, 0, 0], // TODO: Resolve target ID to position
                  fov: 60, // TODO: Add to schema
                  isOrthographic: false,
                };
              }

              // Fallback default
              return {
                position: [0, 1, 5],
                target: [0, 0, 0],
                fov: 60,
                isOrthographic: false,
              };
            },
          },
        },
        splat: {
          entry: () => console.log('Studio Machine: Invoking Splat'),
          invoke: {
            id: 'splat',
            src: 'splatMachine',
            input: ({ context }: { context: StudioContext }) => {
              const objectNode = context.scene?.data.root.nodes.find(
                (n: SceneNode): n is ObjectNode => n.type === 'object'
              );
              return objectNode ?? null;
            },
          }
        }
      },
    },
    error: {
      on: {
        RETRY_LOAD: 'loading'
      },
    },
    unauthorized: {
      // Terminal state for this session, or allow retry with different credentials if we had that event
    }
  },
});
